// @ts-nocheck
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { dischargeTransitionActions, treatmentPlanActions } from '../../../../../store';
import {
  DischargeSchema,
  initialDischargeCriteria,
} from '../../discharge-transition/validationSchema';
import { getAllInvalidFieldNames } from '../../../../../helpers';
import { TreatmentPlanPages } from '../../constants/general';

import type { RootState } from '../../../../../store';
import type { IDischargeCriteria, IDischargeTransitionReason } from '../../../../../utils/types';

function useValidateDischargeTransistions() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { selected: selectedClient } = useSelector((state: RootState) => state.clients);

  const {
    dischargeCriteria,
    reasons,
    treatmentPlanId: currentTPId,
  } = useSelector((state: RootState) => state.dischargeTransitions);

  const planId = treatmentPlan?.id;

  const [isValid, setIsValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [treatmentPlanId, setTreatmentPlanId] = useState<number>();

  useEffect(() => {
    if (!planId) return;
    setTreatmentPlanId(planId);
  }, [planId]);

  useEffect(() => {
    if (!treatmentPlanId || !selectedClient?.id) return;
    dispatch(
      dischargeTransitionActions.updateState({ key: 'triggerValidationsOnPageLoad', data: true }),
    );
    if (currentTPId === treatmentPlanId) {
      const dischargeCriteriaData =
        Object.keys(dischargeCriteria).length > 0
          ? dischargeCriteria
          : null;
      const reasonsData = reasons?.length ? reasons : [];
      fetchAndValidateData(dischargeCriteriaData, reasonsData);
    } else {
      fetchAndValidateData(null, []);
    }
    dispatch(
      dischargeTransitionActions.updateState({ key: 'treatmentPlanId', data: treatmentPlanId }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlanId, selectedClient]);

  async function fetchAndValidateData(
    dischargeCriteriaData: IDischargeCriteria | null,
    transitoinsReasonsData: IDischargeTransitionReason[],
  ) {
    setIsLoading(true);
    let dischargeData = dischargeCriteriaData;
    let reasonsData = transitoinsReasonsData;

    if (!dischargeCriteriaData) {
      const response = await dispatch(
        dischargeTransitionActions.getDischarge({
          treatmentPlanId,
          getAccessTokenSilently,
          forceUpdate: true,
        }),
      );
      if (response.payload) {
        dischargeData = response.payload;
      }
    }

    if (dischargeData?.id && !reasonsData.length) {
      const response = await dispatch(
        dischargeTransitionActions.getReasons({
          treatmentPlanId,
          dischargeId: dischargeData.id,
          getAccessTokenSilently,
          forceUpdate: true,
        }),
      );
      reasonsData = response.payload;
    } else {
      dispatch(dischargeTransitionActions.updateState({ key: 'reasons', data: [] }));
    }
    setIsLoading(false);
    validate(dischargeData, reasonsData);
  }

  const validate = (
    dischargeData: IDischargeCriteria | null,
    reasonsData: IDischargeTransitionReason[],
  ) => {
    let errorCount = 0;
    let dischargeErrors: Record<string, boolean> = {};

    if (!dischargeData) {
      dischargeErrors = getAllInvalidFieldNames(DischargeSchema, initialDischargeCriteria);
    } else {
      const data = { ...initialDischargeCriteria, ...dischargeData };
      delete data.id;
      dischargeErrors = getAllInvalidFieldNames(DischargeSchema, data);
    }

    errorCount += Object.keys(dischargeErrors).length;
    if (dischargeData?.id && reasonsData) {
      errorCount += reasonsData.length ? 0 : 1;
    }
    setIsValid(errorCount === 0 ? true : false);
    dispatch(
      treatmentPlanActions.updateBadge({
        [TreatmentPlanPages.DischargeTransition]: errorCount,
      }),
    );
  };

  return isValid && !isLoading;
}

export default useValidateDischargeTransistions;
